import { useEffect, useState, type FormEvent } from 'react';
import { Box, Plus, Pencil, Trash2, Loader2, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePlatformPackages,
  useCreatePackage,
  useUpdatePackage,
  useDeletePackage,
  type PlatformPackage,
  type PackageInput,
} from '@/hooks/usePlatformPackages';
import { ApiError } from '@/lib/api-client';
import { PackageUsageEstimate } from '@/components/PackageUsageEstimate';
import { useConfirm } from '@/components/ConfirmDialog';

function formatUSD(cents: number | null) {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PlatformPackagesPage() {
  const { data, isPending } = usePlatformPackages();
  const createMut = useCreatePackage();
  const updateMut = useUpdatePackage();
  const deleteMut = useDeletePackage();
  const confirm = useConfirm();

  const [editor, setEditor] = useState<{ mode: 'create' } | { mode: 'edit'; pkg: PlatformPackage } | null>(null);

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Box className="w-5 h-5" /> Paket Yönetimi
          </h1>
          <p className="text-muted-foreground text-sm">
            Paketleri oluştur, fiyatlarını değiştir, Stripe price ID'sini bağla. Silmek yerine <strong>pasifleştirme</strong> önerilir
            (eski orderlar için FK korunur).
          </p>
        </div>
        <Button onClick={() => setEditor({ mode: 'create' })}>
          <Plus className="w-4 h-4" />
          Yeni Paket
        </Button>
      </div>

      {isPending && <div className="text-muted-foreground text-sm">Yükleniyor…</div>}

      {data && (
        <div className="grid gap-3">
          {data.packages.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground text-sm">
                Henüz paket yok. "Yeni Paket" ile başla.
              </CardContent>
            </Card>
          )}
          {data.packages.map((p) => (
            <Card key={p.id} className={!p.enabled ? 'opacity-60' : undefined}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <span>{p.name}</span>
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">{p.id}</code>
                  {!p.enabled && (
                    <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded">Pasif</span>
                  )}
                  {p.sort_order !== 0 && (
                    <span className="text-xs text-muted-foreground">#{p.sort_order}</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid sm:grid-cols-2 gap-3 text-sm mb-3">
                  <Stat label="Fiyat" value={formatUSD(p.price_usd_cents)} />
                  <Stat label="Yüklenen bakiye" value={`$${(p.credits / 1_000_000).toFixed(2)}`} />
                </div>
                <PackageUsageEstimate balanceMicroUsd={p.credits} />
                {p.description && <p className="text-sm text-muted-foreground mt-3 mb-3">{p.description}</p>}
                {p.stripe_price_id && (
                  <div className="text-xs text-muted-foreground mb-3">
                    Stripe Price ID: <code className="bg-muted px-1 rounded">{p.stripe_price_id}</code>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditor({ mode: 'edit', pkg: p })}>
                    <Pencil className="w-3.5 h-3.5" />
                    Düzenle
                  </Button>
                  {!!p.enabled && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Paketi pasifleştir?',
                          description: `"${p.name}" paketi müşterilere görünmez olur. Mevcut satın almalar etkilenmez. Daha sonra tekrar aktifleştirebilirsin.`,
                          confirmText: 'Pasifleştir',
                          danger: true,
                        });
                        if (!ok) return;
                        deleteMut.mutate(p.id);
                      }}
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Pasifleştir
                    </Button>
                  )}
                  {!p.enabled && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateMut.mutate({ id: p.id, enabled: true })}
                      disabled={updateMut.isPending}
                    >
                      Aktifleştir
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editor && (
        <PackageEditor
          initial={editor.mode === 'edit' ? editor.pkg : null}
          onClose={() => setEditor(null)}
          onSubmit={async (body) => {
            if (editor.mode === 'create') {
              await createMut.mutateAsync(body);
            } else {
              const { id: _drop, ...patch } = body;
              await updateMut.mutateAsync({ id: editor.pkg.id, ...patch });
            }
            setEditor(null);
          }}
          submitting={createMut.isPending || updateMut.isPending}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function PackageEditor({
  initial,
  onClose,
  onSubmit,
  submitting,
}: {
  initial: PlatformPackage | null;
  onClose: () => void;
  onSubmit: (body: PackageInput) => Promise<void>;
  submitting: boolean;
}) {
  const [id, setId] = useState(initial?.id ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [priceUsd, setPriceUsd] = useState<string>(
    initial?.price_usd_cents != null ? (initial.price_usd_cents / 100).toFixed(2) : '',
  );
  // Balance granted to the user's wallet, expressed in USD for the form;
  // converted to µUSD on submit (×1_000_000) to match the DB column.
  const [creditsUsd, setCreditsUsd] = useState<string>(
    initial ? (initial.credits / 1_000_000).toFixed(2) : '0.00',
  );
  // Duration kept as a hidden value for schema backward-compat; UI no longer
  // exposes it. On edit we preserve whatever's already saved (so 30-day
  // addons stay 30 instead of getting clobbered to 36500). New rows fall
  // back to a long horizon since balance packs never expire.
  const durationDays = String(initial?.duration_days ?? 36500);
  const [featuresJson, setFeaturesJson] = useState(initial?.features_json ?? '{}');
  const [stripePriceId, setStripePriceId] = useState(initial?.stripe_price_id ?? '');
  const [sortOrder, setSortOrder] = useState<string>(initial ? initial.sort_order.toString() : '0');
  const [enabled, setEnabled] = useState<boolean>(initial ? !!initial.enabled : true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // validate features_json
      if (featuresJson.trim()) {
        try {
          JSON.parse(featuresJson);
        } catch {
          setError("Features JSON geçersiz — { anahtar: değer } formatında yaz");
          return;
        }
      }
      await onSubmit({
        id,
        name,
        description: description || undefined,
        // TRY price deprecated — always send null going forward so we stop
        // maintaining two prices per package.
        price_try_kurus: null,
        price_usd_cents: priceUsd ? Math.round(parseFloat(priceUsd) * 100) : null,
        credits: Math.round((parseFloat(creditsUsd) || 0) * 1_000_000),
        duration_days: parseInt(durationDays, 10) || 36500,
        features_json: featuresJson || undefined,
        stripe_price_id: stripePriceId || undefined,
        sort_order: parseInt(sortOrder, 10) || 0,
        enabled,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <CardTitle>{initial ? `${initial.name} — Düzenle` : 'Yeni Paket'}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <form onSubmit={submit}>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pid">ID (URL'de görünür) *</Label>
                <Input
                  id="pid"
                  value={id}
                  onChange={(e) => setId(e.target.value.toLowerCase())}
                  placeholder="starter"
                  required
                  disabled={!!initial}
                />
                <p className="text-xs text-muted-foreground">Sadece a-z, 0-9, _, - içerir. Oluşturduktan sonra değişmez.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pname">Görünen Ad *</Label>
                <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pdesc">Açıklama</Label>
              <Input id="pdesc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pusd">Fiyat (USD) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    id="pusd"
                    type="number"
                    step="0.01"
                    min="0"
                    value={priceUsd}
                    onChange={(e) => setPriceUsd(e.target.value)}
                    placeholder="9.99"
                    required
                    className="pl-6"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Kullanıcı bu kadar öder.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pcred">Yüklenecek Bakiye (USD) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                  <Input
                    id="pcred"
                    type="number"
                    step="0.01"
                    min="0"
                    value={creditsUsd}
                    onChange={(e) => setCreditsUsd(e.target.value)}
                    required
                    className="pl-6"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Fiyattan yüksekse bonus, düşükse kâr marjı. Kullanıcı bu
                  bakiyeyi süresiz kullanır.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pstripe">Stripe Price ID</Label>
                <Input
                  id="pstripe"
                  value={stripePriceId}
                  onChange={(e) => setStripePriceId(e.target.value)}
                  placeholder="price_…"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="psort">Sıralama (küçük üstte)</Label>
                <Input
                  id="psort"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pfeat">Features JSON</Label>
              <textarea
                id="pfeat"
                value={featuresJson}
                onChange={(e) => setFeaturesJson(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                placeholder='{"byok":true,"max_sites":10,"team_editors":3}'
              />
              <p className="text-xs text-muted-foreground">Pricing sayfasında görünecek özellikler.</p>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="penabled"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="penabled" className="cursor-pointer">
                Aktif (müşteriler satın alabilir)
              </Label>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}
          </CardContent>
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Kaydet
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
